import _ from 'lodash'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useIsMobile } from '@/hooks/useSizeBreakpoints'
import { FieldArray, Form, Formik, FormikProps } from 'formik'
import * as yup from 'yup'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import FormHelperText from '@mui/material/FormHelperText'
import IconButton from '@mui/material/IconButton'
import LoadingButton from '@mui/lab/LoadingButton'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import AddIcon from '@mui/icons-material/Add'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'

import { DayRangePicker } from '@/components/ui/inputs/DayRangePicker'
import ErrorMessage from '@/components/formik/messages/ErrorMessage'

dayjs.extend(duration)
dayjs.extend(isSameOrAfter)

export type SelectTimeSlotHandle = {
  toggleEditable: () => void
}

export type SelectTimeSlotValues = {
  slots: { start: Date; end: Date }[]
}

type SelectTimeSlotProps = {
  initialValues: SelectTimeSlotValues
  isSubmitting?: boolean
  onSelectTimeSlot?: (values: SelectTimeSlotValues) => void
}

export const SelectTimeSlot = React.forwardRef(function SelectTimeSlot(props: SelectTimeSlotProps, ref: React.Ref<SelectTimeSlotHandle>) {
  const { initialValues, isSubmitting = false, onSelectTimeSlot } = props
  const { t } = useTranslation()

  useImperativeHandle(ref, () => ({
    toggleEditable,
  }))

  const formikRef = useRef<FormikProps<SelectTimeSlotValues>>(null)

  const [editable, setEditable] = useState(false)

  const isTooSmall = useIsMobile()

  const selectTimeSlotFormSchema = useMemo(() => {
    return yup.object().shape({
      slots: yup
        .array()
        .of(
          yup
            .object()
            .shape({
              start: yup.date().required(),
              end: yup
                .date()
                .required()
                .test('is-greater', t('error.time.endTimeSoonerThanStartTime.message'), (value, testContext) => {
                  const { start } = testContext.parent
                  return dayjs(value).isSameOrAfter(dayjs(start))
                }),
            })
            .test('is-not-overlapping', { end: t('error.time.overlap.message') }, (value, testContext) => {
              const currentSlot = value
              const otherSlots = _.differenceWith(testContext.parent, [currentSlot], _.isEqual)
              for (const otherSlot of otherSlots) {
                if (dayjs(currentSlot.start).isBefore(dayjs(otherSlot.end)) && dayjs(currentSlot.end).isAfter(dayjs(otherSlot.start))) {
                  return false
                }
              }
              return true
            })
        )
        .required(),
    })
  }, [t])

  useEffect(() => {}, [])

  const handleGenerateTimeSlot = (values: SelectTimeSlotValues) => {
    onSelectTimeSlot?.(values)
  }

  const toggleEditable = () => {
    setEditable((prevEditable) => !prevEditable)
  }

  return (
    <Stack justifyContent="start" height="100%">
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        onSubmit={handleGenerateTimeSlot}
        validationSchema={selectTimeSlotFormSchema}
        enableReinitialize
      >
        {({ dirty, isValid, handleReset, setFieldValue, submitForm, values, errors }) => (
          <Form>
            <Stack spacing={3}>
              <FieldArray
                name="slots"
                render={(arrayHelpers) =>
                  values.slots && values.slots.length > 0 ? (
                    <Stack spacing={3}>
                      {values.slots.map((slot, index) => {
                        const error = _.get(errors, `slots[${index}].end`)
                        return (
                          <Box key={`${slot.start.getTime()}-${slot.end.getTime()}-${index}`}>
                            {!isTooSmall ? (
                              <Stack direction="row" alignItems="end" justifyContent="space-between" spacing={1}>
                                <Stack direction="row" alignItems="end" spacing={2}>
                                  <DayRangePicker
                                    readOnly={initialValues.slots.length ? !editable : false}
                                    onChange={(startDate, endDate) => setFieldValue(`slots[${index}]`, { start: startDate, end: endDate })}
                                    startDate={dayjs(slot.start).toDate()}
                                    endDate={dayjs(slot.end).toDate()}
                                  />
                                  {(editable || !initialValues.slots.length) && (
                                    <Tooltip title={t('common:button.delete.tooltip')}>
                                      <IconButton
                                        onClick={() => {
                                          arrayHelpers.remove(index)
                                        }}
                                      >
                                        <DeleteRoundedIcon />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Stack>
                                {(editable || !initialValues.slots.length) && index === values.slots.length - 1 && (
                                  <Tooltip title={t('common:button.add.tooltip')}>
                                    <IconButton
                                      onClick={() =>
                                        arrayHelpers.push({
                                          start: dayjs(_.last(values.slots)?.start).add(1, 'day').startOf('day').add(8, 'hours').toDate(),
                                          end: dayjs(_.last(values.slots)?.end).add(1, 'day').endOf('day').toDate(),
                                        })
                                      }
                                    >
                                      <AddIcon />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Stack>
                            ) : (
                              <Stack direction="column" alignItems="start" justifyContent="space-between" spacing={1}>
                                <DayRangePicker
                                  readOnly={initialValues.slots.length ? !editable : false}
                                  onChange={(startDate, endDate) => setFieldValue(`slots[${index}]`, { start: startDate, end: endDate })}
                                  startDate={dayjs(slot.start).toDate()}
                                  endDate={dayjs(slot.end).toDate()}
                                />
                                <Stack direction="row" alignItems="end" spacing={2}>
                                  {(editable || !initialValues.slots.length) && (
                                    <Tooltip title={t('common:button.delete.tooltip')}>
                                      <IconButton
                                        onClick={() => {
                                          arrayHelpers.remove(index)
                                        }}
                                      >
                                        <DeleteRoundedIcon />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                  {(editable || !initialValues.slots.length) && index === values.slots.length - 1 && (
                                    <Tooltip title={t('common:button.add.tooltip')}>
                                      <IconButton
                                        onClick={() =>
                                          arrayHelpers.push({
                                            start: dayjs(_.last(values.slots)?.start).add(1, 'day').startOf('day').add(8, 'hours').toDate(),
                                            end: dayjs(_.last(values.slots)?.end).add(1, 'day').endOf('day').toDate(),
                                          })
                                        }
                                      >
                                        <AddIcon />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Stack>
                              </Stack>
                            )}
                            {error && (
                              <FormHelperText error sx={{ position: 'unset' }}>
                                <ErrorMessage error={error} />
                              </FormHelperText>
                            )}
                          </Box>
                        )
                      })}
                    </Stack>
                  ) : (
                    <Stack direction="row" justifyContent="end">
                      <Tooltip title={t('common:button.add.tooltip')}>
                        <IconButton
                          onClick={() =>
                            arrayHelpers.push({
                              start: dayjs().startOf('day').add(8, 'hours').toDate(),
                              end: dayjs().endOf('day').toDate(),
                            })
                          }
                        >
                          <AddIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  )
                }
              />
              {((!!initialValues.slots.length && editable) || (!initialValues.slots.length && dirty)) && (
                <Stack direction="row" justifyContent="start" spacing={1}>
                  <LoadingButton disabled={!isValid} loading={isSubmitting} onClick={submitForm} variant="contained">
                    {t('page.configuration.calendso.availability.button.confirm.label')}
                  </LoadingButton>
                  <Button
                    onClick={() => {
                      handleReset()
                      if (!!initialValues.slots.length) toggleEditable()
                    }}
                    variant="neutral"
                  >
                    {t('common:button.cancel.label')}
                  </Button>
                </Stack>
              )}
              {!!initialValues.slots.length && !editable && (
                <Stack direction="row" justifyContent="start">
                  <Button onClick={toggleEditable} variant="contained">
                    {t('common:button.modify.label')}
                  </Button>
                </Stack>
              )}
            </Stack>
          </Form>
        )}
      </Formik>
    </Stack>
  )
})

export default SelectTimeSlot
